import { Flex } from '@brightdrop/bd-ui';
import { cx } from '@emotion/css';
import { Box, Typography } from '@mui/material';
import VehicleImageLoader from '~/common/components/VehicleImageLoader/VehicleImageLoader';
import { DEFAULT_EMPTY_VALUE } from '~/common/constants/common.constant';

import useStyles from './VehicleIdentityCellRenderer.styles';

export interface VehicleIdentityCellRendererProps {
  vin: string;
  name?: string;
  vehicleTypeDetail?: string;
  imageUrl: string;
  onClick?: () => void;
  isMobileView?: boolean;
  /**
   * When false, the image block is hidden entirely if no real imageUrl is available,
   * instead of showing the default ghost vehicle placeholder.
   * Defaults to true to preserve existing behavior across the platform.
   */
  showPlaceholderImage?: boolean;
}

const VehicleIdentityCellRenderer = ({
  vin,
  name,
  vehicleTypeDetail,
  imageUrl,
  onClick,
  isMobileView,
  showPlaceholderImage = true,
}: VehicleIdentityCellRendererProps) => {
  const { classes } = useStyles();
  const showImage = !isMobileView && (showPlaceholderImage || !!imageUrl);

  return (
    <Box
      className={cx(
        classes.vehicleInfoWrapper,
        isMobileView ? 'infoAlignRight' : ''
      )}
      style={{ cursor: onClick ? 'pointer' : 'inherit' }}
      onClick={onClick}
      data-testid="vehicle-identity-cell"
    >
      {showImage && (
        <Box className="vehicleImg" data-testid="vehicle-image">
          <VehicleImageLoader src={imageUrl} />
        </Box>
      )}
      <Box className={cx('assetInfo', { noImage: !showImage })}>
        <Box className={`assetInfo-name ${isMobileView ? 'mobileView' : ''}`}>
          <Box
            data-heap-redact-attributes="id"
            id={`asset-name_${name}`}
            className="vehicle-column-text-ellipsis"
          >
            <Typography variant="small_body_medium" data-heap-redact-text>
              <span data-testid="vehicle-list-assetName-value">
                {name ? name : DEFAULT_EMPTY_VALUE}
              </span>
            </Typography>
          </Box>
        </Box>
        {vin && (
          <Flex className={`${isMobileView ? 'mobileView' : ''}`}>
            <Typography
              className={classes.vehicleDetails}
              variant="caption_regular"
              data-testid={`vehicle-list-vin-value`}
            >
              {`${vin}`}
            </Typography>
          </Flex>
        )}
        <Box
          className={`vehicle-column-text-ellipsis ${isMobileView ? 'mobileView' : ''}`}
        >
          <Typography
            className={classes.vehicleDetails}
            variant="caption_regular"
            data-testid={`vehicle-list-vehicleTypeDetail-value`}
          >
            {vehicleTypeDetail ? vehicleTypeDetail : DEFAULT_EMPTY_VALUE}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default VehicleIdentityCellRenderer;
